import { useCallback, useEffect, useState } from "react";
import api, { formatApiError } from "../lib/api";
import { toast } from "sonner";
import { TASK_CODE_LABELS } from "./WrapQuestionBank";

export function WrapMonitoringEditor({ clientId }) {
  const [data, setData] = useState(null);
  const [showAvailable, setShowAvailable] = useState(false);
  const [busy, setBusy] = useState("");

  const load = useCallback(() => {
    api.get(`/clients/${clientId}/wrap-questions`).then((r) => setData(r.data))
      .catch((e) => toast.error(formatApiError(e)));
  }, [clientId]);
  useEffect(() => { load(); }, [load]);

  if (!data) return null;
  const activeBank = (data.bank || []).filter((q) => q.active);
  const assigned = new Set(data.assigned || []);
  const disabled = new Set(data.disabled || []);
  const careCodes = new Set((data.care_codes || []).map(String));
  const autoQs = activeBank.filter((q) => q.task_code && careCodes.has(String(q.task_code)));
  const manualQs = activeBank.filter((q) => !q.task_code || !careCodes.has(String(q.task_code)));
  const assignedManual = manualQs.filter((q) => assigned.has(q.id));
  const availableManual = manualQs.filter((q) => !assigned.has(q.id));
  const suggestions = data.suggestions || [];
  if (activeBank.length === 0 && suggestions.length === 0) return null;

  const save = async (nextAssigned, nextDisabled) => {
    setData((cur) => ({ ...cur, assigned: [...nextAssigned], disabled: [...nextDisabled] }));
    try {
      await api.put(`/clients/${clientId}/wrap-questions`, { question_ids: [...nextAssigned], disabled_ids: [...nextDisabled] });
      toast.success("Wrap-up questions saved");
    } catch (e) {
      toast.error(formatApiError(e));
      load();
    }
  };

  const toggle = (qid) => {
    const next = new Set(assigned);
    if (next.has(qid)) next.delete(qid); else next.add(qid);
    save(next, disabled);
  };
  const toggleAuto = (qid) => {
    const next = new Set(disabled);
    if (next.has(qid)) next.delete(qid); else next.add(qid);
    save(assigned, next);
  };

  const analyze = async () => {
    setBusy("analyze");
    try {
      const r = await api.post(`/admin/clients/${clientId}/wrap-questions/suggest`);
      const n = (r.data.suggestions || []).length;
      toast.success(n ? `${n} suggestion${n === 1 ? "" : "s"} from ${r.data.notes_analyzed} recent notes` : "Analysis done — current questions fit the recent notes");
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy("");
    }
  };

  const decide = async (sid, action) => {
    setBusy(sid);
    try {
      await api.put(`/admin/clients/${clientId}/wrap-questions/suggestions/${sid}`, { action });
      toast.success(action === "approve" ? "Suggestion applied" : "Suggestion dismissed");
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy("");
    }
  };

  const pill = (q, on, onClick, tid, offStyle) => (
    <button key={q.id} type="button" onClick={onClick}
      className={`rounded-full border px-3 py-1 text-xs font-medium transition-colors ${on ? "border-[#2F6B4F] bg-[#2F6B4F] text-white" : offStyle || "border-stone-300 bg-white text-stone-500 hover:border-stone-400"}`}
      title={q.prompt}
      data-testid={tid}>
      {q.task_code ? `${TASK_CODE_LABELS[String(q.task_code)] || q.task_code}: ` : ""}{q.prompt.length > 38 ? q.prompt.slice(0, 38) + "…" : q.prompt}
    </button>
  );

  return (
    <div className="mt-2 rounded-lg border border-[#2F6B4F]/25 bg-[#2F6B4F]/[0.04] p-3" data-testid="wrap-monitoring-editor">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <p className="text-[11px] font-semibold uppercase tracking-wider text-[#2F6B4F]">
          Wrap-up monitoring — asked every shift for {data.client_name}
        </p>
        <button type="button" onClick={analyze} disabled={busy === "analyze"}
          className="rounded-full border border-[#2F6B4F]/40 px-3 py-1 text-[11px] font-semibold text-[#2F6B4F] hover:bg-[#2F6B4F]/10 disabled:opacity-40"
          data-testid="wrap-analyze-btn">
          {busy === "analyze" ? "Analyzing recent notes…" : "Analyze notes for suggestions"}
        </button>
      </div>

      {suggestions.length > 0 && (
        <div className="mt-2 rounded-md border border-[#C49A3A]/40 bg-[#C49A3A]/[0.06] p-2.5" data-testid="wrap-suggestions">
          <p className="text-[10px] font-semibold uppercase tracking-wider text-[#8a6a14]">Suggested from recent notes — your call</p>
          <div className="mt-1.5 space-y-2">
            {suggestions.map((s) => (
              <div key={s.id} className="flex flex-wrap items-center justify-between gap-2" data-testid={`wrap-suggestion-${s.id}`}>
                <div className="min-w-0">
                  <p className="text-xs font-medium text-stone-800">
                    {s.action === "add" ? "Add: " : "Remove: "}{s.prompt}
                  </p>
                  {s.reason && <p className="text-[11px] text-stone-500">{s.reason}</p>}
                </div>
                <div className="flex shrink-0 gap-1.5">
                  <button type="button" onClick={() => decide(s.id, "approve")} disabled={busy === s.id}
                    className="rounded-full bg-[#2F6B4F] px-2.5 py-0.5 text-[11px] font-semibold text-white hover:opacity-90 disabled:opacity-40"
                    data-testid={`wrap-suggestion-approve-${s.id}`}>Approve</button>
                  <button type="button" onClick={() => decide(s.id, "dismiss")} disabled={busy === s.id}
                    className="rounded-full border border-stone-300 px-2.5 py-0.5 text-[11px] font-semibold text-stone-500 hover:bg-white disabled:opacity-40"
                    data-testid={`wrap-suggestion-dismiss-${s.id}`}>Dismiss</button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {autoQs.length > 0 && (
        <div className="mt-2" data-testid="wrap-auto-modules">
          <p className="text-[10px] font-semibold uppercase tracking-wider text-stone-400">From the care plan — these task codes are on it</p>
          <div className="mt-1.5 flex flex-wrap gap-2">
            {autoQs.map((q) => pill(q, !disabled.has(q.id), () => toggleAuto(q.id), `wrap-auto-${q.id}`,
              "border-stone-300 bg-white text-stone-400 line-through hover:border-stone-400"))}
          </div>
          <p className="mt-1 text-[10px] text-stone-400">On automatically because the task is on this client's care plan — tap to turn off for this client.</p>
        </div>
      )}

      {assignedManual.length > 0 && (
        <div className="mt-2" data-testid="wrap-assigned-questions">
          <p className="text-[10px] font-semibold uppercase tracking-wider text-stone-400">Decided for this client</p>
          <div className="mt-1.5 flex flex-wrap gap-2">
            {assignedManual.map((q) => pill(q, true, () => toggle(q.id), `wrap-assigned-${q.id}`))}
          </div>
        </div>
      )}

      {availableManual.length > 0 && (
        <div className="mt-2" data-testid="wrap-available-questions">
          <button type="button" onClick={() => setShowAvailable((v) => !v)}
            className="text-[11px] font-semibold text-stone-500 hover:text-stone-800 hover:underline"
            data-testid="wrap-available-toggle">
            {showAvailable ? "Hide" : "Show"} {availableManual.length} more available question{availableManual.length === 1 ? "" : "s"}
          </button>
          {showAvailable && (
            <div className="mt-1.5 flex flex-wrap gap-2">
              {availableManual.map((q) => pill(q, false, () => toggle(q.id), `wrap-assign-${q.id}`))}
            </div>
          )}
        </div>
      )}
      <p className="mt-1.5 text-[10px] text-stone-400">Manage the question bank in Settings → Wrap-up question bank.</p>
    </div>
  );
}
